import { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  IconDashboard, IconUsers, IconTx, IconDevice,
  IconConfig, IconChart, IconLogout, IconWifi, IconSub, IconEye, IconClose, IconMail, IconPlan, IconGift,
} from './Icons';

// Building icon for lodges
const IconBuilding = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10" />
  </svg>
);

const NAV = [
  { to: '/',              label: 'Dashboard',     Icon: IconDashboard },
  { to: '/users',         label: 'Users',         Icon: IconUsers     },
  { to: '/transactions',  label: 'Transactions',  Icon: IconTx        },
  { to: '/subscriptions', label: 'Subscriptions', Icon: IconSub       },
  { to: '/plans',         label: 'Plans',         Icon: IconPlan      },
  { to: '/devices',       label: 'Devices',       Icon: IconDevice    },
  { to: '/lodges',        label: 'Lodges',        Icon: IconBuilding  },
  { to: '/access-points', label: 'Access Points', Icon: IconWifi      },
  { to: '/outages',       label: 'Outages',       Icon: IconClose     },
  { to: '/credits',       label: 'Free Hours',    Icon: IconGift      },
  { to: '/payments',      label: 'Payment Lookup',Icon: IconTx        },
  { to: '/sessions',      label: 'Connection Logs',Icon: IconEye      },
  { to: '/analytics',     label: 'Analytics',     Icon: IconChart     },
  { to: '/config',        label: 'Site Settings', Icon: IconConfig    },
  { to: '/broadcast',     label: 'Email',         Icon: IconMail      },
  { to: '/logs',          label: 'Audit Logs',    Icon: IconEye       },
];

export default function Sidebar({ open, onClose }) {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { onClose?.(); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <>
      <aside style={{
        width: 230, flexShrink: 0,
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, bottom: 0, left: 0,
        zIndex: 200, transition: 'transform 0.25s ease',
        overflowY: 'auto',
      }}
        className={`sidebar ${open ? 'sidebar-open' : ''}`}
      >
        {/* Header */}
        <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
              <IconWifi size={16} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: 'var(--text)', lineHeight: 1 }}>WiFi Portal</div>
              <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}>Admin Console</div>
            </div>
          </div>
          <button onClick={onClose} className="sidebar-close"
            style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 4, display: 'none' }}>
            <IconClose size={18} />
          </button>
        </div>

        {/* Nav links */}
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {NAV.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} end={to === '/'}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px 10px 20px',
                color: isActive ? 'var(--accent)' : 'var(--text2)',
                background: isActive ? 'var(--accent-dim)' : 'transparent',
                borderLeft: `3px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
                fontWeight: isActive ? 600 : 400,
                fontSize: 13, textDecoration: 'none', transition: 'all 0.12s',
              })}
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '6px 4px' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {admin?.email?.[0]?.toUpperCase() || 'A'}
            </div>
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{admin?.name || 'Admin'}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{admin?.email}</div>
            </div>
          </div>
          <button onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; e.currentTarget.style.background = 'var(--red-dim)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <IconLogout size={13} /> Sign Out
          </button>
        </div>
      </aside>

      <style>{`
        @media (max-width: 768px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.sidebar-open { transform: translateX(0); animation: slideIn 0.25s ease; }
          .sidebar-close { display: flex !important; }
        }
        @media (min-width: 769px) {
          .sidebar { transform: translateX(0) !important; }
        }
      `}</style>
    </>
  );
}